import { actionRegistry, conditionRegistry } from './registries.js';
/**
 * Step statuses that mean the pipeline did something WRONG: a thrown
 * error / unknown node / max-steps (`failed`), a business failure outcome
 * (`outcome_failed`), or an action that could not run for lack of an adapter
 * (`outcome_skipped_no_adapter`). The "nothing-to-do" outcomes
 * (`outcome_no_user`, `outcome_already_deleted`, `outcome_already_cancelled`,
 * `outcome_no_subscription`, `outcome_cancelled`, `outcome_queued`) are NOT
 * errors. Canonical set — reused by the admin API, the rail UI, and SQL
 * (build `IN`-lists from this array; never re-hard-code the strings).
 */
export const PIPELINE_ERROR_STEP_STATUSES = [
    'failed',
    'outcome_failed',
    'outcome_skipped_no_adapter',
];
/** True when a step status is in {@link PIPELINE_ERROR_STEP_STATUSES}. */
export const isErrorStepStatus = (status) => PIPELINE_ERROR_STEP_STATUSES.includes(status);
/**
 * Hard cap on the cause text. It lands in `pipeline_run.error`, which the admin
 * rail renders inline — same budget as `describeGoogleError`'s.
 */
const STEP_CAUSE_MAX_LENGTH = 500;
const isRecord = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const asNonEmptyString = (v) => {
    if (typeof v !== 'string')
        return null;
    const trimmed = v.trim();
    return trimmed.length > 0 ? trimmed : null;
};
/**
 * Collect candidate cause fragments off a step `result`, headline first.
 *
 * Order is "outcome marker, then elaboration": a domain outcome (`cancel` /
 * `delete` / `resolve`) names WHAT failed, `detail` / `error` say WHY. `error`
 * comes first only when there is no domain marker, because a thrown executor
 * error IS the headline in that case — the join below de-duplicates either way.
 */
const collectCauseParts = (result) => {
    const parts = [];
    const push = (v) => {
        const s = asNonEmptyString(v);
        if (s)
            parts.push(s);
    };
    if (typeof result === 'string') {
        push(result);
        return parts;
    }
    if (!isRecord(result))
        return parts;
    // Domain outcome markers written by the action registry
    // (`registries.ts` cancel_subscription / delete_account / resolve_identity).
    push(result.cancel);
    push(result.delete);
    push(result.resolve);
    // Thrown-executor message (runner's catch) and the runner's own synthetic
    // failures (`max_steps_exceeded`, `node_not_found`).
    push(result.error);
    // Free-text elaboration from the adapters, plus the cycle-guard's `reason`.
    push(result.detail);
    push(result.reason);
    return parts;
};
/**
 * One-line human-facing cause for a FAILED pipeline step, derived from its
 * `result` jsonb.
 *
 * Why this exists: `pipeline_run.error` used to be the bare string
 * `failed at node <nodeId>`, so a run whose EMAIL SEND died on `invalid_grant`
 * read as "failed at node no_user" — the node id is the graph author's label,
 * not the cause, and 21 prod runs were mis-read that way. The cause is not lost
 * (it lives in `pipeline_run_step.result`), it was simply never lifted onto the
 * run row.
 *
 * Устройство копирует `describeGoogleError`
 * (`packages/channel-gmail/src/errors.ts:26-110`): guarded accessors, fragments
 * collected general → specific, containment de-dup, `: ` join, hard cap,
 * mandatory fallback. The CODE is not shared: `describeGoogleError` is bound to
 * Google/Gaxios error shapes and lives in a channel ADAPTER, which the pipeline
 * core may not import (hexagonal boundary).
 */
export const describeStepFailure = (result, fallback = 'причина не записана') => {
    const parts = collectCauseParts(result);
    const kept = [];
    for (const part of parts) {
        // Drop a fragment that adds nothing: already present verbatim, or wholly
        // contained in something we already kept.
        if (kept.some((k) => k === part || k.includes(part)))
            continue;
        // If the new part SUBSUMES an earlier one, replace the earlier one in place.
        const subsumedIdx = kept.findIndex((k) => part.includes(k));
        if (subsumedIdx >= 0) {
            kept[subsumedIdx] = part;
            continue;
        }
        kept.push(part);
    }
    if (kept.length === 0)
        return fallback;
    const joined = kept.join(': ');
    return joined.length > STEP_CAUSE_MAX_LENGTH
        ? `${joined.slice(0, STEP_CAUSE_MAX_LENGTH - 1)}…`
        : joined;
};
/**
 * Canonical `pipeline_run.error` line: `${type} @ ${nodeId}: ${cause}`.
 *
 * ONE format, written from BOTH terminal-finish sites (the auto runner and the
 * resume handler) so the rail never has to guess which producer wrote a row.
 * The node TYPE leads because it is what an operator recognises ("отправка
 * шаблона письма"); the node id follows for pinpointing the graph position.
 * Capped at 500 like the cause itself.
 */
export const formatRunError = (step) => {
    const line = `${step.type} @ ${step.nodeId}: ${describeStepFailure(step.result)}`;
    return line.length > STEP_CAUSE_MAX_LENGTH
        ? `${line.slice(0, STEP_CAUSE_MAX_LENGTH - 1)}…`
        : line;
};
/**
 * ENGINE RULE — where a run must CONTINUE when a step blew up: the `escalated`
 * edge the graph author declared on the very node that failed, or `null` when
 * there is none (⇒ the caller keeps the terminal-`failed` behaviour).
 *
 * WHY. A throw inside an action records a `failed` step and stops the walk. For a
 * node whose author declared no escalation that is the right ending. For a node
 * that HAS an `escalated` edge it is a contradiction: the author already wrote
 * down where to go when this step does not deliver what the graph needs, and a
 * failure is the strongest possible case of not delivering it. The damage is
 * worst where the failed node was also the one that would have PARKED the run —
 * the question never reached the customer, the wait-timeout sweeper only ever
 * scans `status='waiting'` and so can never see this run, and the escalation
 * target is reachable ONLY through a resume that will never happen. Total
 * silence: no customer answer, no operator signal.
 *
 * Deliberately stated over the GRAPH, not over a node type. Nothing here mentions
 * `collect_input`: the rule is «declared an escalation ⇒ gets it», so any node
 * type added later — a payment retry, an upstream call, a second kind of waiting
 * node — inherits the behaviour by declaring the edge, with no change to this
 * function. It also cannot change any existing graph's behaviour by surprise: a
 * node with no `escalated` key reads as `null`, exactly as before.
 *
 * The failed step STAYS in the audit — this is a continuation, not a retry and
 * not a cover-up: the rail still shows ⚠.
 */
export const escalationTargetForFailedStep = (def, result) => {
    if (result.status !== 'failed')
        return null;
    const failed = result.steps.find((s) => s.status === 'failed');
    if (!failed)
        return null;
    const node = def.nodes[failed.nodeId];
    if (node?.kind !== 'action')
        return null;
    // Structural read: any action node whose `outcomes` map declares `escalated`.
    // `outcomes` is optional on some node types and keyed by domain outcomes on
    // others (`cancelled`, `no_subscription`, …) — none of which is `escalated`,
    // so this cannot silently re-route today's cancel/delete failures.
    const outcomes = node.outcomes;
    const escalated = outcomes?.escalated;
    return typeof escalated === 'string' && escalated.length > 0 ? escalated : null;
};
/** Hard cap on visited nodes — a final guard beyond the `visited` cycle set. */
export const DEFAULT_MAX_STEPS = 100;
/**
 * Pure pipeline interpreter. Walks the node graph from `def.entryNodeId`:
 *  - condition node → evaluate via the registry, record `branch_true` /
 *    `branch_false`, follow `onTrue` / `onFalse` (null → stop, completed).
 *  - action node → execute via the registry, record `ok` + the result, follow
 *    `next` (null → stop, completed).
 *
 * Safety: a `visited` set guards cycles (a re-visit records a `skipped` step and
 * stops); `maxSteps` caps total visits. An executor that throws records a
 * `failed` step and stops with `status: 'failed'` — the throw does NOT escape
 * `runPipeline` (the caller decides what to do; rethrowing would re-run prior
 * actions on redelivery). A missing/unknown node id likewise records `failed`.
 * Liveness: when `ctx.runStatus` is wired, every action node is preceded by a
 * live read of the run's own status; anything but `'running'` records a
 * `skipped` step (`reason: 'run_cancelled'`) and stops `completed` — see
 * {@link RunStatusPort}.
 */
export const runPipeline = async (def, ctx, opts = {}) => {
    const maxSteps = opts.maxSteps ?? DEFAULT_MAX_STEPS;
    const steps = [];
    const visited = new Set();
    let cursor = opts.startNodeId ?? def.entryNodeId;
    while (cursor !== null) {
        if (steps.length >= maxSteps) {
            steps.push({
                nodeId: cursor,
                kind: 'action',
                type: 'unknown',
                status: 'failed',
                result: { error: 'max_steps_exceeded' },
            });
            return { steps, status: 'failed' };
        }
        if (visited.has(cursor)) {
            steps.push({
                nodeId: cursor,
                kind: 'action',
                type: 'unknown',
                status: 'skipped',
                result: { reason: 'cycle_detected' },
            });
            return { steps, status: 'completed' };
        }
        visited.add(cursor);
        const node = def.nodes[cursor];
        if (!node) {
            steps.push({
                nodeId: cursor,
                kind: 'action',
                type: 'unknown',
                status: 'failed',
                result: { error: 'node_not_found' },
            });
            return { steps, status: 'failed' };
        }
        if (node.kind === 'condition') {
            let branch;
            try {
                branch = await conditionRegistry[node.type](node, ctx);
            }
            catch (err) {
                steps.push({
                    nodeId: node.id,
                    kind: 'condition',
                    type: node.type,
                    status: 'failed',
                    result: { error: err instanceof Error ? err.message : String(err) },
                });
                return { steps, status: 'failed' };
            }
            steps.push({
                nodeId: node.id,
                kind: 'condition',
                type: node.type,
                status: branch ? 'branch_true' : 'branch_false',
                result: { branch },
            });
            cursor = branch ? node.onTrue : node.onFalse;
            continue;
        }
        // action node
        let execution;
        try {
            // LIVENESS — before the side effect, never after. A merge may have
            // cancelled this run while the previous node was executing; see
            // `RunStatusPort`. A throwing read lands in the `failed` branch below, the
            // same as a throwing executor: fail-stop, never fail-open.
            if (ctx.runStatus !== undefined) {
                const live = await ctx.runStatus.read();
                if (live !== 'running') {
                    steps.push({
                        nodeId: node.id,
                        kind: 'action',
                        type: node.type,
                        status: 'skipped',
                        result: { reason: 'run_cancelled', runStatus: live },
                    });
                    return { steps, status: 'completed' };
                }
            }
            execution = await actionRegistry[node.type](node, ctx);
        }
        catch (err) {
            // A genuine thrown error (a bug) → record failed and stop.
            steps.push({
                nodeId: node.id,
                kind: 'action',
                type: node.type,
                status: 'failed',
                result: { error: err instanceof Error ? err.message : String(err) },
            });
            return { steps, status: 'failed' };
        }
        // Park branch: executor signals the run should suspend here and wait for
        // an external event (e.g. a customer reply to a collect_input question).
        if (execution.waiting === true) {
            steps.push({
                nodeId: node.id,
                kind: 'action',
                type: node.type,
                status: 'waiting',
                result: execution.result,
            });
            return { steps, status: 'waiting', awaitNodeId: node.id };
        }
        // collect_input / resolve_identity route via their `outcomes` map (always
        // present on these node types, never have a `next` sibling field).
        if (node.type === 'collect_input' || node.type === 'resolve_identity') {
            // Programmer-error guard: an executor for these types MUST return an outcome.
            // A missing outcome is a registry bug — throw (don't swallow into a failed
            // step) so it surfaces as a clear assertion failure in tests and logs.
            if (execution.outcome === undefined) {
                throw new Error(`executor for node '${node.id}' (type '${node.type}') must return an outcome`);
            }
            const outcome = execution.outcome;
            steps.push({
                nodeId: node.id,
                kind: 'action',
                type: node.type,
                status: `outcome_${outcome}`,
                result: execution.result,
            });
            cursor = node.outcomes[outcome] ?? null;
            continue;
        }
        // cancel_subscription / delete_account branch on their outcome; every other
        // action is a straight `ok → next`.
        if ((node.type === 'cancel_subscription' || node.type === 'delete_account') &&
            execution.outcome !== undefined) {
            const outcome = execution.outcome;
            const hasOutcomesMap = node.outcomes !== undefined;
            const outcomeMapped = hasOutcomesMap && Object.prototype.hasOwnProperty.call(node.outcomes, outcome);
            // BACK-COMPAT: with NO `outcomes` map, the failure outcome(s) STOP the run
            // with a `failed` step (never let a downstream confirmation email follow a
            // failed cancel/delete). cancel_subscription stops on `failed` /
            // `skipped_no_adapter`; delete_account stops on `failed` (its `no_user` is
            // success-ish — nothing to delete — and falls through). Only the
            // success-ish outcomes fall through to `next`. With an explicit map the
            // operator opts into routing every outcome.
            const isStopOutcome = node.type === 'cancel_subscription'
                ? outcome === 'failed' || outcome === 'skipped_no_adapter'
                : outcome === 'failed';
            if (!hasOutcomesMap && isStopOutcome) {
                steps.push({
                    nodeId: node.id,
                    kind: 'action',
                    type: node.type,
                    status: 'failed',
                    result: execution.result,
                });
                return { steps, status: 'failed' };
            }
            steps.push({
                nodeId: node.id,
                kind: 'action',
                type: node.type,
                status: `outcome_${outcome}`,
                result: execution.result,
            });
            // Next cursor: the outcome's edge if the key is PRESENT in the map (even a
            // null value → end of branch), else fall back to `next`. `node.outcomes` is
            // a union of the two partial-record maps and `outcome` the combined outcome
            // union, so TS can't prove the key indexes the matching map; `outcomeMapped`
            // already established presence via hasOwnProperty, so a record-cast is safe.
            const outcomesMap = node.outcomes;
            cursor = outcomeMapped ? (outcomesMap[outcome] ?? null) : node.next;
            continue;
        }
        steps.push({
            nodeId: node.id,
            kind: 'action',
            type: node.type,
            // D2: an executor may downgrade a plain action to `'skipped'` when a
            // safeguard deliberately did not perform it (`result.suppressed` says why).
            // The walk CONTINUES to `next` either way — a suppressed send must not
            // truncate the graph, or a cancel confirmation node after it would be lost.
            status: execution.status ?? 'ok',
            result: execution.result,
        });
        cursor = node.next;
    }
    return { steps, status: 'completed' };
};
//# sourceMappingURL=runner.js.map